'use client';

import { useMemo, useState, type ChangeEvent, type ReactNode } from 'react';
import { useOthers } from '@liveblocks/react';
import { Badge, DataTable, Dialog, Inline, Input, Stack, Typography, type Column } from '@org/design-system';
import type { ShadcnTableConfig, ShadcnTableColumn } from './types';
import { useCollaboratorSelections } from './collaboration/useCollaboratorSelections';
import { useSelectedRowPresence } from './collaboration/PresenceProvider';
import { useCollaborationIdentity } from './collaboration/PresenceProvider';
import { getCollaboratorColor } from './collaboration/presence';

interface TableRendererProps {
  config: ShadcnTableConfig;
  rowActions?: (row: Record<string, unknown>) => ReactNode;
  onRowFocus?: (rowId: string | null) => void;
}

export type ColumnDef<T extends Record<string, unknown>> = Column<T>;

function toColumnDef(
  column: ShadcnTableColumn,
  isFirstColumn: boolean,
  getRowHighlightColor: (row: Record<string, unknown>) => string | null,
): ColumnDef<Record<string, unknown>> {
  return {
    key: column.key,
    header: column.header,
    sortable: column.sortable,
    render: (row: Record<string, unknown>) => {
      const value = row[column.key];
      const content = column.render
        ? column.render(value, row)
        : (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value ?? ''));
      const highlightColor = getRowHighlightColor(row);

      return (
        <div className={isFirstColumn ? 'relative ps-2' : undefined}>
          {isFirstColumn && highlightColor ? (
            <span
              aria-hidden="true"
              className="absolute bottom-0 start-0 top-0 w-1 rounded-sm"
              style={{ backgroundColor: highlightColor }}
            />
          ) : null}
          {content}
        </div>
      );
    },
  };
}

export function TableRenderer({ config, rowActions, onRowFocus }: TableRendererProps) {
  const { enabled } = useCollaborationIdentity();
  const others = useOthers();
  const [query, setQuery] = useState('');
  const [selectedRow, setSelectedRow] = useState<Record<string, unknown> | null>(null);
  const collaboratorSelections = useCollaboratorSelections();
  const setSelectedRowPresence = useSelectedRowPresence();
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | undefined>();
  const initialPageSize = config.pagination?.pageSize ?? 10;
  const [page, setPage] = useState(config.pagination?.pageIndex ?? 0);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const activeRowByCollaborator = useMemo(() => others.reduce<Record<string, string>>((acc, collaborator) => {
    const selectedRowId = collaborator.presence.selectedRowId;
    const color = (collaborator.info as { color?: string } | undefined)?.color ?? getCollaboratorColor(collaborator.connectionId);
    if (selectedRowId) {
      acc[selectedRowId] = color;
    }
    return acc;
  }, {}), [others]);

  const filteredData = useMemo(() => {
    if (!query.trim()) {
      return config.data;
    }
    const normalized = query.toLowerCase();
    return config.data.filter((row) => {
      return config.columns.some((column) => {
        if (!column.filterable && column.filterable !== undefined) {
          return false;
        }
        const cellValue = row[column.key];
        return String(cellValue ?? '').toLowerCase().includes(normalized);
      });
    });
  }, [config.columns, config.data, query]);

  const sortedData = useMemo(() => {
    if (!sortConfig) {
      return filteredData;
    }

    const sorted = [...filteredData];
    sorted.sort((a, b) => {
      const left = a[sortConfig.key];
      const right = b[sortConfig.key];
      if (left == null && right == null) {
        return 0;
      }
      if (left == null) {
        return 1;
      }
      if (right == null) {
        return -1;
      }
      const result = String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
      return sortConfig.direction === 'asc' ? result : -result;
    });

    return sorted;
  }, [filteredData, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const paginatedData = useMemo(() => {
    const start = currentPage * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [currentPage, pageSize, sortedData]);

  const getRowHighlightColor = (row: Record<string, unknown>) => {
    const rowId = row[config.keyField ?? 'id'];
    if (rowId == null) {
      return null;
    }
    return collaboratorSelections.get(String(rowId)) ?? null;
  };

  const baseColumns = useMemo(() => config.columns.map((column, index) => toColumnDef(column, index === 0, getRowHighlightColor)), [config.columns, collaboratorSelections]);

  const pendingSyncColumn = useMemo<ColumnDef<Record<string, unknown>>>(() => ({
    key: '__pending_sync',
    header: 'Sync',
    render: (row) => (row.__pendingSync ? <Badge variant="warning">Pending sync</Badge> : null),
  }), []);

  const columns = useMemo<ColumnDef<Record<string, unknown>>[]>(() => {
    const resolvedColumns: ColumnDef<Record<string, unknown>>[] = [];

    // Add presence indicator column if collaboration is enabled
    if (enabled) {
      resolvedColumns.push({
        key: '__presence',
        header: '',
        width: 8,
        render: (row) => {
          const rowKeyField = config.keyField ?? 'id';
          const rowId = String(row[rowKeyField] ?? '');
          const activeColor = activeRowByCollaborator[rowId];
          return (
            <div
              className="h-6 w-1 rounded-full"
              style={{ backgroundColor: activeColor ?? 'transparent' }}
              aria-hidden="true"
            />
          );
        },
      });
    }

    // Add pending sync column
    resolvedColumns.push(pendingSyncColumn);

    // Add base columns
    resolvedColumns.push(...baseColumns);

    // Add actions column if provided
    if (rowActions) {
      resolvedColumns.push({
        key: '__actions',
        header: 'Actions',
        render: (row) => rowActions(row),
      });
    }

    return resolvedColumns;
  }, [activeRowByCollaborator, baseColumns, config.keyField, enabled, pendingSyncColumn, rowActions]);

  return (
    <Stack gap={4}>
      {(config.title || config.description) && (
        <Stack gap={1}>
          {config.title ? <Typography variant="h3">{config.title}</Typography> : null}
          {config.description ? <Typography color="muted">{config.description}</Typography> : null}
        </Stack>
      )}

      <DataTable
        onRowClick={(row) => {
          const keyField = config.keyField ?? 'id';
          const rowId = row[keyField];
          setSelectedRow(row);
          setSelectedRowPresence(rowId == null ? null : String(rowId));
          onRowFocus?.(String(row[keyField] ?? ''));
        }}
        data={paginatedData}
        keyExtractor={config.keyField ?? 'id'}
        columns={columns}
        sortConfig={sortConfig}
        onSort={(key: string) => {
          setSortConfig((previous: { key: string; direction: 'asc' | 'desc' } | undefined) => {
            if (!previous || previous.key !== key) {
              return { key: String(key), direction: 'asc' };
            }
            return {
              key: previous.key,
              direction: previous.direction === 'asc' ? 'desc' : 'asc',
            };
          });
        }}
        emptyMessage="No rows found"
        toolbar={{
          search: config.searchable ? (
            <Input
              value={query}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setPage(0);
                setQuery(event.target.value);
              }}
              placeholder="Search rows"
              aria-label="Search rows"
            />
          ) : undefined,
          actions: (
            <Inline gap={2} align="center">
              <label className="text-sm text-muted-foreground" htmlFor="page-size-select">Rows</label>
              <select
                id="page-size-select"
                className="rounded-[var(--ds-radius-md)] border border-border bg-background px-2 py-1 text-sm"
                value={pageSize}
                onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                  setPage(0);
                  setPageSize(Number(event.target.value));
                }}
              >
                {[5, 10, 20, 50].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </Inline>
          ),
        }}
        pagination={{
          page: currentPage + 1,
          totalPages,
          onPageChange: (nextPage: number) => setPage(Math.max(0, nextPage - 1)),
        }}
      />

      <Dialog
        isOpen={Boolean(selectedRow)}
        onClose={() => {
          setSelectedRow(null);
          setSelectedRowPresence(null);
        }}
        title="Row details"
        description="Live selection is shared with collaborators."
      >
        <pre className="max-h-[60vh] overflow-auto rounded-md border border-border bg-muted p-3 text-xs text-foreground">
          {JSON.stringify(selectedRow, null, 2)}
        </pre>
      </Dialog>
    </Stack>
  );
}
